import React, { useState, useCallback } from 'react';
import OptimizedImage from './OptimizedImage';

/**
 * PhotoGrid Component
 * 
 * A performance-optimized photo grid with:
 * - Batched loading (Load More)
 * - Responsive grid layout
 * - Optimized thumbnails
 * - Click to open lightbox
 */
const PhotoGrid = ({
  photos = [],
  initialBatchSize = 24,
  batchSize = 24,
  onPhotoClick,
  albumTitle = 'Album',
}) => {
  const [displayCount, setDisplayCount] = useState(initialBatchSize);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const displayedPhotos = photos.slice(0, displayCount);
  const hasMore = displayCount < photos.length;
  const remainingCount = photos.length - displayCount;

  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);
    // Small delay for better UX feedback
    setTimeout(() => {
      setDisplayCount((prev) => Math.min(prev + batchSize, photos.length));
      setIsLoadingMore(false);
    }, 300);
  }, [batchSize, photos.length]);

  if (photos.length === 0) {
    return (
      <div className="text-center py-20">
        <svg
          className="w-24 h-24 mx-auto mb-6 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-gray-600">Er zijn nog geen foto's toegevoegd aan dit album.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Photo count indicator */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          {displayedPhotos.length} van {photos.length} foto's
        </p>
      </div>

      {/* Photo Grid */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
        data-testid="photos-grid"
      >
        {displayedPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className="group cursor-pointer rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300"
            onClick={() => onPhotoClick && onPhotoClick(photo, index)}
            data-testid={`photo-${index}`}
          >
            <OptimizedImage
              src={photo.image_url}
              alt={photo.caption || `Foto ${index + 1} van ${albumTitle}`}
              variant="thumb"
              sizeType="grid"
              priority={index < 8} // First 8 images load immediately
              className="transform group-hover:scale-105 transition-transform duration-300"
            />
            {/* Optional caption overlay */}
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-sm truncate">{photo.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-8 text-center">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="inline-flex items-center gap-2 bg-[#0a56c2] hover:bg-[#083d8a] text-white px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            data-testid="load-more-btn"
          >
            {isLoadingMore ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Laden...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                Laad meer foto's ({remainingCount} resterend)
              </>
            )}
          </button>
        </div>
      )}

      {/* All loaded message */}
      {!hasMore && photos.length > initialBatchSize && (
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            ✓ Alle {photos.length} foto's geladen
          </p>
        </div>
      )}
    </div>
  );
};

export default PhotoGrid;
